import React from "react";
import { Spinner } from "@chakra-ui/react";
import { FiInfo, FiSend } from "react-icons/fi";
import { Button } from "newui/components/Button";
import type { Clarification } from "../../types/clarification.type";
import { Modal } from "newui/components/ui/Modal";

interface SendResponsesModalProps {
	clarifications: Clarification[];
	isLoading?: boolean;
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => Promise<void>;
}

export const SendResponsesModal: React.FC<SendResponsesModalProps> = ({
	isOpen,
	onClose,
	onConfirm,
	clarifications,
	isLoading = false,
}) => {
	const handleConfirm = async () => {
		await onConfirm();
	};

	const truncateText = (text: string, maxLength: number) => {
		if (!text) {
			return "";
		}
		return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
	};

	const FooterComponent = () => {
		return (
			<div className="flex w-full justify-between gap-2">
				<Button onClick={onClose} btntype="base" className="!w-full" disabled={isLoading}>
					Cancel
				</Button>
				<Button className="!w-full" onClick={handleConfirm} disabled={isLoading}>
					{isLoading ? <Spinner size="sm" /> : "Send Responses"}
				</Button>
			</div>
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Send Responses to Client"
			description={`Send ${clarifications.length} response${clarifications.length !== 1 ? "s" : ""} to client`}
			customIcon={<FiSend size={30} className="stroke-blue-500" />}
			size="lg"
			customFooter={<FooterComponent />}
		>
			<div className="space-y-4">
				<p className="text-sm text-gray-600">
					You are about to send {clarifications.length} response{clarifications.length !== 1 ? "s" : ""} to the client.
					Once sent, these clarification responses cannot be modified.
				</p>

				<div className="max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
					<h3 className="mb-3 text-sm font-semibold text-gray-900">Responses to Send:</h3>
					<ul className="space-y-2">
						{clarifications.map((c) => (
							<li key={c.id} className="text-sm">
								<span className="font-medium text-gray-900">#{c.reference_number}</span>
								{" - "}
								<span className="text-gray-600">
									{truncateText(c.clarification_text, 100)}
								</span>
							</li>
						))}
					</ul>
				</div>

				<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
					<div className="flex items-start gap-2">
						<FiInfo className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
						<p className="text-sm text-blue-800">
							The client will receive one email notification with all responses.
						</p>
					</div>
				</div>
			</div>
		</Modal>
	);
};
